import React, { useState } from "react";
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  UilShieldCheck,
  UilLock,
  UilEnvelope,
  UilCheckCircle,
  UilAngleDown,
  UilAngleUp,
} from '@iconscout/react-unicons';
import Privacy from "../assets/Images/Privacy.webp";
import FooterPage from "./FooterPage";

const Section = ({ title, children, icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const toggleSection = () => {
    setIsOpen(!isOpen);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="mb-4"
    >
      <div
        className="flex justify-between items-center cursor-pointer bg-white border-2 border-gray-200 hover:border-blue-500 p-6 rounded-xl transition-all shadow-sm hover:shadow-md"
        onClick={toggleSection}
      >
        <div className="flex items-center gap-3">
          {icon && <div className="text-blue-600">{icon}</div>}
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
        </div>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-blue-600"
        >
          {isOpen ? <UilAngleUp size={28} /> : <UilAngleDown size={28} />}
        </motion.div>
      </div>
      <motion.div
        initial={false}
        animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="p-6 bg-gray-50 rounded-b-xl border-2 border-t-0 border-gray-200 mt-[-8px]">
          {children}
        </div>
      </motion.div>
    </motion.div>
  );
};

const PrivacyPolicy = () => {
  return (
    <div className="bg-gray-50 min-h-screen">
      <Helmet>
        <title>Privacy Policy - Fabiana Fabulous Cleaning Services</title>
        <meta name="description" content="Privacy Policy for Fabiana Fabulous Cleaning Services LLC. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://fabycleaners.com/privacy" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative min-h-[60vh] flex items-center bg-cover bg-center bg-fixed pt-20"
        style={{ backgroundImage: `url(${Privacy})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/90 via-purple-900/85 to-blue-800/90" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full mb-6"
            >
              <UilShieldCheck size={48} className="text-yellow-400" />
            </motion.div>

            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Privacy <span className="text-yellow-400">Policy</span>
            </h1>

            <p className="text-xl md:text-2xl mb-4 text-gray-100 max-w-3xl mx-auto">
              Your privacy is important to us
            </p>
            <p className="text-lg text-gray-300">
              Last updated: January 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-20">
        {/* Introduction */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 bg-white rounded-2xl p-8 shadow-lg"
        >
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white">
              <UilCheckCircle size={24} />
            </div>
            <h2 className="text-3xl font-bold">Introduction</h2>
          </div>
          <p className="text-gray-700 text-lg leading-relaxed">
            Fabiana Fabulous Cleaning Services LLC ("we," "us," "our") is committed to protecting
            your privacy. This Privacy Policy explains how we collect, use, and
            share information when you use our website and services.
          </p>
        </motion.section>

        {/* Accordion Sections */}
        <div className="space-y-4">
          <Section
            title="Information We Collect"
            icon={<UilLock size={24} />}
          >
            <p className="text-gray-700 mb-4 text-lg">
              We may collect and process the following types of personal information:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Contact Information:</strong>
                  <span className="text-gray-700"> Your name, email address, phone number, and any other information you provide when submitting a request or contacting us.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Usage Information:</strong>
                  <span className="text-gray-700"> Data on how you interact with our website, such as IP addresses, device information, and browsing patterns.</span>
                </div>
              </li>
            </ul>
          </Section>

          <Section
            title="How We Use Your Information"
            icon={<UilShieldCheck size={24} />}
          >
            <p className="text-gray-700 mb-4 text-lg">
              We use your information for purposes including:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Responding to Your Requests:</strong>
                  <span className="text-gray-700"> To provide the services or information you request.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Marketing Communications:</strong>
                  <span className="text-gray-700"> With your consent, we may send you promotional offers, updates, and other relevant information.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Improving Our Services:</strong>
                  <span className="text-gray-700"> To analyze and improve our website and services.</span>
                </div>
              </li>
            </ul>
          </Section>

          <Section
            title="How We Share Your Information"
            icon={<UilLock size={24} />}
          >
            <p className="text-gray-700 mb-4 text-lg font-semibold">
              We do not sell or share your personal information.
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Service Providers:</strong>
                  <span className="text-gray-700"> We may share your data with trusted partners who assist us in providing our services.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Legal Requirements:</strong>
                  <span className="text-gray-700"> We may disclose your information to comply with legal obligations.</span>
                </div>
              </li>
            </ul>
          </Section>

          <Section
            title="Your Choices"
            icon={<UilCheckCircle size={24} />}
          >
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Opt-Out:</strong>
                  <span className="text-gray-700"> You may opt out of marketing communications at any time by clicking the "unsubscribe" link in our emails or by contacting us directly.</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <UilCheckCircle className="text-green-500 flex-shrink-0 mt-1" size={20} />
                <div>
                  <strong className="text-gray-900">Access and Correction:</strong>
                  <span className="text-gray-700"> You may request access to your personal information and ask us to update, correct, or delete it.</span>
                </div>
              </li>
            </ul>
          </Section>

          <Section
            title="Security"
            icon={<UilShieldCheck size={24} />}
          >
            <p className="text-gray-700 text-lg leading-relaxed">
              We take reasonable measures to protect your information. However, no
              online transmission is entirely secure, and we cannot guarantee
              absolute security.
            </p>
          </Section>

          <Section
            title="Changes to This Policy"
            icon={<UilCheckCircle size={24} />}
          >
            <p className="text-gray-700 text-lg leading-relaxed">
              We may update this policy occasionally. Changes will be posted on
              this page with an updated effective date.
            </p>
          </Section>

          <Section
            title="Contact Us"
            icon={<UilEnvelope size={24} />}
          >
            <p className="text-gray-700 mb-4 text-lg">
              If you have questions about this policy, please contact us at:
            </p>
            <div className="bg-white rounded-lg p-6 border-2 border-gray-200">
              <p className="font-bold text-gray-900 mb-2">Fabiana Fabulous Cleaning Services LLC</p>
              <a
                href="mailto:fabulous@fabycleaners.com"
                className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
              >
                <UilEnvelope size={20} />
                fabulous@fabycleaners.com
              </a>
            </div>
          </Section>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl p-8 text-center text-white shadow-xl"
        >
          <UilShieldCheck size={48} className="mx-auto mb-4" />
          <h3 className="text-2xl font-bold mb-3">Your Privacy Matters</h3>
          <p className="text-lg text-gray-100 mb-6">
            We are committed to protecting your personal information and being transparent about our practices.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 px-8 py-3 bg-white hover:bg-gray-100 text-blue-600 font-bold rounded-lg shadow-lg transition-all"
          >
            <UilEnvelope size={24} />
            Contact Us with Questions
          </a>
        </motion.div>
      </div>

      <FooterPage />
    </div>
  );
};

export default PrivacyPolicy;
